import {DialogProvider, TabPanel, TabProvider} from '@ariakit/react'
import {zodResolver} from '@hookform/resolvers/zod'
import {useState} from 'react'
import {Controller, useForm} from 'react-hook-form'
import {
    Dialog,
    DialogBody,
    DialogDismiss,
    DialogFooter,
    DialogHeader,
    DialogHeading
} from '@/components/ui/dialog-atoms/DialogAtoms'
import {Button} from '@/components/ui/button/Button'
import {Spinner} from '@/components/ui/spinner/Spinner'
import {Send01Icon} from '@/components/ui/icon/Icon'
import {useAuthStore} from '@/features/auth/store/store'
import {useTranslation} from '@/translations/i18n'
import {BugReportFormSchema, BugReportFormValues, EnvironmentSnapshot} from '../../types'
import {BugReportHistory} from '../BugReportHistory/BugReportHistory'
import {RichTextEditor} from '../RichTextEditor/RichTextEditor'
import {
    StyledFieldError,
    StyledFieldGroup,
    StyledFieldLabel,
    StyledForm,
    StyledReporterInfo,
    StyledReporterRow,
    StyledRequiredMark,
    StyledSubmitError,
    StyledTab,
    StyledTabList,
    StyledTextInput
} from './style'

type BugReporterTab = 'form' | 'history'

interface BugReporterDialogProps {
    isOpen: boolean
    onClose: () => void
    onSubmit: (values: BugReportFormValues, snapshot: EnvironmentSnapshot) => Promise<unknown>
    isSubmitting: boolean
    submitError: string | null
    getSnapshot: () => EnvironmentSnapshot
}

export const BugReporterDialog = ({
    isOpen,
    onClose,
    onSubmit,
    isSubmitting,
    submitError,
    getSnapshot
}: BugReporterDialogProps) => {
    const {t} = useTranslation()
    const [editorResetKey, setEditorResetKey] = useState(0)
    const [activeTab, setActiveTab] = useState<BugReporterTab>('form')
    const user = useAuthStore(state => state.user)
    const reporterName = user?.fullName || t('bug_reporter:unknown_reporter')
    const reporterEmail = user?.email || t('bug_reporter:unknown_reporter')

    const form = useForm<BugReportFormValues>({
        resolver: zodResolver(BugReportFormSchema),
        defaultValues: {
            title: '',
            description: ''
        }
    })

    const handleClose = () => {
        if (isSubmitting) return
        form.reset()
        setEditorResetKey(k => k + 1)
        setActiveTab('form')
        onClose()
    }

    const handleSubmit = form.handleSubmit(async values => {
        const snapshot = getSnapshot()
        const result = await onSubmit(values, snapshot)
        if (result) {
            form.reset()
            setEditorResetKey(k => k + 1)
        }
    })

    return (
        <DialogProvider open={isOpen} setOpen={open => (open ? null : handleClose())}>
            <Dialog width="860px">
                <DialogHeader>
                    <DialogHeading>{t('bug_reporter:title')}</DialogHeading>
                    <DialogDismiss />
                </DialogHeader>
                <TabProvider selectedId={activeTab} setSelectedId={id => id && setActiveTab(id as BugReporterTab)}>
                    <StyledTabList aria-label={t('bug_reporter:tabs_label')}>
                        <StyledTab id="form">{t('bug_reporter:tab_form')}</StyledTab>
                        <StyledTab id="history">{t('bug_reporter:tab_history')}</StyledTab>
                    </StyledTabList>

                    <TabPanel tabId="form">
                        <StyledForm onSubmit={handleSubmit} noValidate>
                            <DialogBody gap={5}>
                                <StyledReporterInfo>
                                    <StyledReporterRow>
                                        <span>{t('bug_reporter:reporter_label')}</span>
                                        <span>{reporterName}</span>
                                    </StyledReporterRow>
                                    <StyledReporterRow>
                                        <span>{t('commons:email')}</span>
                                        <span>{reporterEmail}</span>
                                    </StyledReporterRow>
                                </StyledReporterInfo>

                                <Controller
                                    control={form.control}
                                    name="title"
                                    render={({field, fieldState}) => (
                                        <StyledFieldGroup>
                                            <StyledFieldLabel htmlFor="bug-reporter-title">
                                                {t('bug_reporter:title_field_label')}
                                                <StyledRequiredMark>*</StyledRequiredMark>
                                            </StyledFieldLabel>
                                            <StyledTextInput
                                                id="bug-reporter-title"
                                                placeholder={t('bug_reporter:title_field_placeholder')}
                                                autoFocus
                                                {...field}
                                            />
                                            {fieldState.error?.message && (
                                                <StyledFieldError role="alert">
                                                    {fieldState.error.message}
                                                </StyledFieldError>
                                            )}
                                        </StyledFieldGroup>
                                    )}
                                />

                                <Controller
                                    control={form.control}
                                    name="description"
                                    render={({field}) => (
                                        <StyledFieldGroup>
                                            <StyledFieldLabel>
                                                {t('bug_reporter:description_field_label')}
                                            </StyledFieldLabel>
                                            <RichTextEditor
                                                onChange={field.onChange}
                                                placeholder={t('bug_reporter:description_field_placeholder')}
                                                resetKey={editorResetKey}
                                            />
                                        </StyledFieldGroup>
                                    )}
                                />

                                {submitError && <StyledSubmitError role="alert">{submitError}</StyledSubmitError>}
                            </DialogBody>

                            <DialogFooter>
                                <Button type="button" variant="secondary" onClick={handleClose} disabled={isSubmitting}>
                                    {t('commons:cancel')}
                                </Button>
                                <Button type="submit" variant="primaryNeutral" disabled={isSubmitting}>
                                    {isSubmitting ? (
                                        <>
                                            <Spinner size={16} />
                                            {t('bug_reporter:submitting')}
                                        </>
                                    ) : (
                                        <>
                                            <Send01Icon size={16} />
                                            {t('bug_reporter:submit')}
                                        </>
                                    )}
                                </Button>
                            </DialogFooter>
                        </StyledForm>
                    </TabPanel>

                    <TabPanel tabId="history">
                        <DialogBody gap={5}>
                            <BugReportHistory enabled={activeTab === 'history'} />
                        </DialogBody>
                    </TabPanel>
                </TabProvider>
            </Dialog>
        </DialogProvider>
    )
}
